/**
 * @fileoverview Histórico das operações da conexão (mais recentes primeiro),
 * inclusive as tentativas negadas por senha/frase. Paginação por cursor.
 */

import React from "react";
import { Loader2 } from "lucide-react";

import useStyles from "./styles";
import { StatusBadge } from "./parts";
import { filtersSummary, fmtDateTime, fmtNumber, t } from "./format";

const HistoryItem = ({ op, canResume, onResume }) => {
	const classes = useStyles();
	const denied = op.status === "denied";
	const unit = op.operation === "delete_contacts" ? "Contacts" : "Tickets";
	// Resolver não apaga mensagens: o detalhe é só o que foi preservado.
	const detailsKey = op.operation === "resolve_tickets" ? "history.detailsResolve" : `history.details${unit}`;

	return (
		<div className={classes.historyItem}>
			<div style={{ minWidth: 0 }}>
				<div className={classes.historyTitle}>
					<span>{t(`history.operation.${op.operation}`)}</span>
					<StatusBadge status={op.status} />
				</div>
				<div className={classes.historyFilters}>{filtersSummary(op.operation, op.filters)}</div>
				<div className={classes.historyMeta}>
					{t("history.by", { user: op.userName || "—", date: fmtDateTime(op.createdAt) })}
					{op.resumedAt
						? ` · ${t("progress.resumedBy", { user: op.resumedByName || "—", date: fmtDateTime(op.resumedAt) })}`
						: ""}
					{op.cancelRequestedAt
						? ` · ${t("progress.cancelRequestedBy", {
								user: op.cancelRequestedByName || "—",
								date: fmtDateTime(op.cancelRequestedAt),
						  })}`
						: ""}
				</div>
			</div>
			<div className={classes.historyCounts}>
				{denied ? (
					t(`history.denied.${op.errorCode}`, { defaultValue: t("statusLabel.denied") })
				) : (
					<>
						{t(`history.counts${unit}`, {
							processed: fmtNumber(op.processedCount),
							total: fmtNumber(op.totalCount),
						})}
						<span className={classes.historyCountsSub}>
							{t(detailsKey, {
								skipped: fmtNumber(op.skippedCount),
								messages: fmtNumber(op.messagesDeleted),
							})}
						</span>
					</>
				)}
			</div>
			{op.status === "failed" && op.lastError ? <div className={classes.historyError}>{op.lastError}</div> : null}
			{op.status === "failed" && canResume && (
				<div className={classes.historyActions}>
					<button type="button" className={classes.btnGhost} onClick={() => onResume(op)}>
						{t("progress.resume")}
					</button>
				</div>
			)}
		</div>
	);
};

// canResume: false enquanto há operação ativa na conexão — a retomada seria
// recusada depois de pedir a senha.
const HistoryPanel = ({ items, nextCursor, loadingMore, canResume, onLoadMore, onResume }) => {
	const classes = useStyles();

	return (
		<div>
			<p className={classes.intro}>{t("history.intro")}</p>
			{items.length === 0 ? (
				<div className={classes.empty}>{t("history.empty")}</div>
			) : (
				<div className={classes.historyList}>
					{items.map((op) => (
						<HistoryItem key={op.id} op={op} canResume={canResume} onResume={onResume} />
					))}
				</div>
			)}
			{nextCursor && (
				<div className={classes.loadMore}>
					<button type="button" className={classes.btnGhost} onClick={onLoadMore} disabled={loadingMore}>
						{loadingMore && <Loader2 size={14} className={classes.spin} />}
						{t("history.loadMore")}
					</button>
				</div>
			)}
		</div>
	);
};

export default HistoryPanel;
